import { ComponentType } from 'react';

import {
  Button,
  DialogContent,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  TypographyProps,
} from '@mui/material';
import cx from 'classnames';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';

import styles from './NewDashboard.module.scss';
import {
  useCreateDashboard,
  useGetDashboardsUsageLimit,
} from '@/api/REST/dashboards';
import {
  type DashboardTemplate,
  useGetDashboardTemplates,
} from '@/api/typesense/dashboards';
import { PageTitle } from '@/components/global/PageTitle';
import { CardGrid } from '@/components/layouts/CardGrid';
import { PanelContent } from '@/components/layouts/PanelContent';
import { ExpandableCards } from '@/components/ui/ExpandableCards';
import { Icon } from '@/components/ui/Icon';
import { PageHeader } from '@/components/ui/PageHeader';
import { routes } from '@/constants/routes';
import { uiIcons } from '@/constants/uiIcons';
import { useNotification } from '@/hooks/useNotification';
import { DashboardUsageLimitChip } from '@/pages/reporting/DashboardsOverview/components/DashboardUsageLimitChip/DashboardUsageLimitChip.tsx';
import { useLocaleStore } from '@/stores/locale';

const MotionStack = motion.create(Stack);
const MotionTypography = motion.create(
  Typography as ComponentType<TypographyProps>, // otherwise TS complains about non-existing component prop
);

export const NewDashboard = () => {
  const { t } = useTranslation('dashboardCreation');
  const { lang } = useLocaleStore();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { showError } = useNotification();

  const {
    data: { user: userUsageLimit } = {},
    isLoading: isUsageLimitLoading,
    isError: isUsageLimitError,
  } = useGetDashboardsUsageLimit();
  const createDashboard = useCreateDashboard();

  /*
   * Use manual state passed to Link in DashboardTable columns, instead of relying on window.navigation as it is not widely supported yet.
   * Can be refactored once non-supporting Safari versions get less distribution, see https://caniuse.com/wf-navigation
   */
  const backTo = state?.backTo ?? routes.reporting.base();

  const { data: templates, isLoading: isTemplatesLoading } =
    useGetDashboardTemplates();

  const createNewDashboard = async (templateId: string) => {
    const template = templates?.find((t) => t.id === templateId);
    if (!template) {
      console.error('Template not found for id:', templateId);
      return;
    }

    const now = new Date();
    await createDashboard.mutateAsync(
      {
        templateId: Number(template.id),
        name: `${template.title} (${now.toLocaleDateString(lang, { day: '2-digit', month: '2-digit', year: 'numeric' })}, ${now.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })})`,
      },
      {
        onSuccess: (response) => {
          navigate(routes.reporting.dashboard(response.id));
        },
        onError: (error) => {
          console.error('Error creating dashboard:', error);
          showError({ message: t('CreateDashboardError') });
        },
      },
    );
  };

  const isUserAtLimit = userUsageLimit?.canCreate === false;
  const isUserCreateNewDisabled =
    isUsageLimitLoading || isUsageLimitError || isUserAtLimit;

  const renderCardActions = ({ cardId, cardHovered }) => (
    <Tooltip title={isUserAtLimit ? t('CreationDisabledTooltip') : ''}>
      <Button
        className={cx(styles.CardActionButton, {
          [styles.DisplayAsHovered]: cardHovered && !isUserCreateNewDisabled,
        })}
        size="small"
        variant={
          cardHovered && !isUserCreateNewDisabled ? 'contained' : 'outlined'
        }
        color="secondary"
        disabled={isUserCreateNewDisabled}
        onClick={(event) => {
          event.stopPropagation();
          createNewDashboard(cardId);
        }}
      >
        {t('CreateDashboardButton')}
      </Button>
    </Tooltip>
  );

  const renderExpanded = (item: DashboardTemplate) => (
    <DialogContent>
      <Stack gap={2}>
        {/* Additional stack needed to align with dom layout in card for layout animation */}
        <Stack direction="row">
          <MotionTypography
            layoutId={`card-title-${item.id}`}
            variant="h4"
            component="h2"
          >
            {item.title}
          </MotionTypography>
        </Stack>
        <MotionTypography
          layoutId={`card-description-${item.id}`}
          variant="bodyRegular"
        >
          {item.description}
        </MotionTypography>
        <MotionStack
          layoutId={`card-bottom-${item.id}`}
          direction="row"
          alignItems="center"
          justifyContent="end"
        >
          <Tooltip title={isUserAtLimit ? t('CreationDisabledTooltip') : ''}>
            <Button
              size="small"
              variant={'outlined'}
              color="secondary"
              disabled={isUserCreateNewDisabled}
              onClick={(event) => {
                event.stopPropagation();
                createNewDashboard(item.id);
              }}
            >
              {t('CreateDashboardButton')}
            </Button>
          </Tooltip>
        </MotionStack>
      </Stack>
    </DialogContent>
  );

  return (
    <>
      <PageTitle title={t('PageTitle')} />

      <PanelContent>
        <PageHeader
          title={t('PageTitle')}
          titleAdornment={<DashboardUsageLimitChip type="user" />}
          iconName={uiIcons.actions.add}
          subtitle={t('PageSubtitle')}
        >
          <PageHeader.Info>
            <Button
              component={Link}
              to={backTo}
              variant="text"
              color="secondary"
              startIcon={<Icon name={uiIcons.navigation.back} size="medium" />}
            >
              {t('BackNavigationButton')}
            </Button>
          </PageHeader.Info>
        </PageHeader>
        {isTemplatesLoading ? (
          <CardGrid
            items={Array.from({ length: 4 }, (_, i) => ({ id: String(i + 1) }))}
            isLastPage={true}
            showMore={() => {}}
          >
            {(item) => (
              <Skeleton
                key={item.id}
                variant="rounded"
                width="100%"
                height={230}
              />
            )}
          </CardGrid>
        ) : (
          <ExpandableCards
            items={templates ?? []}
            renderCardBottom={renderCardActions}
            renderExpanded={renderExpanded}
          />
        )}
      </PanelContent>
    </>
  );
};
